<script setup>
import { onMounted, ref } from 'vue';
import { RepositoryFactory } from '@/data/repositoryFactory.js';
import {
  copyToClipboard,
  isNonEmptyString,
  isNotEmptyArray,
  trimString
} from '../../helpers/utilsHelper';
import { useRouter } from 'vue-router';

const NOTE_TRIM_LENGTH = 100;

const props = defineProps({ demPointId: Number, demPointTag: String });

const RepositoryDemPoints = RepositoryFactory.get('demPoints');

const duplicates = ref(null);
const isLoading = ref(false);
const router = useRouter();

const columns = [
  { key: 'oznaczenie', header: 'Oznaczenie', tdStyle: 'white-space: nowrap;' },
  {
    key: 'message',
    header: 'Informacja',
    tdStyle: 'white-space: pre;',
    computed: true
  },
  {
    key: 'order_number',
    header: 'Zlecenie',
    tdStyle: 'white-space: nowrap;',
    computed: true
  },
  { key: 'actions', header: 'Akcje', tdStyle: 'white-space: nowrap;' }
];

function fetchData() {
  isLoading.value = true;

  RepositoryDemPoints.getDemPointDuplicates(props.demPointId)
    .then((data) => {
      duplicates.value = data;
    })
    .catch((error) => console.log(error))
    .finally(() => {
      isLoading.value = false;
    });
}

function goToOrderDetails(orderId, actionId = undefined) {
  let routeData = router.resolve({
    name: 'orderDetails',
    params: { id: orderId, actionId: actionId }
  });
  window.open(routeData.href, '_blank');
}

function shouldBeBold(duplicate) {
  return duplicate.oznaczenie === props.demPointTag;
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <div class="ibox">
    <div class="ibox-content p-0" :class="{ 'sk-loading': isLoading }" style="border-style: none">
      <div class="sk-spinner sk-spinner-three-bounce">
        <div class="sk-bounce1"></div>
        <div class="sk-bounce2"></div>
        <div class="sk-bounce3"></div>
      </div>

      <div v-if="isNotEmptyArray(duplicates)" class="table-responsive">
        <table class="table table-striped">
          <thead>
            <tr>
              <th v-for="column in columns" :key="column.key">
                {{ column.header }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(duplicate, index) in duplicates" :key="index">
              <template v-for="column in columns" :key="column.key">
                <td>
                  <!-- Message column -->
                  <template v-if="column.key === 'message'">
                    <span :style="{ fontWeight: shouldBeBold(duplicate) ? 'bold' : 'normal' }">
                      {{ duplicate.message }}
                    </span>
                  </template>

                  <!-- Order number column -->
                  <template v-else-if="column.key === 'order_number'">
                    <a @click="goToOrderDetails(duplicate.other_id_order)" class="text-navy">
                      {{ duplicate.order_number }}
                    </a>
                  </template>

                  <!-- Actions column -->
                  <template v-else-if="column.key === 'actions'">
                    <span v-if="!isNotEmptyArray(duplicate.other_point_has_actions)">-</span>
                    <div
                      v-else
                      style="display: flex; flex-direction: column; gap: 5px; flex-wrap: wrap"
                    >
                      <div
                        v-for="(action, actionIndex) in duplicate.other_point_has_actions"
                        :key="actionIndex"
                        style="display: flex; gap: 1rem"
                      >
                        <a
                          @click="goToOrderDetails(duplicate.other_id_order, action.id)"
                          class="text-navy"
                          style="width: 180px"
                        >
                          {{ action.action_type }}
                        </a>
                        <span
                          v-if="isNonEmptyString(action.note)"
                          :title="action.note.length > NOTE_TRIM_LENGTH ? action.note : ''"
                          :style="action.note.length > NOTE_TRIM_LENGTH ? 'cursor: help;' : ''"
                        >
                          {{ trimString(action.note, NOTE_TRIM_LENGTH) }}
                        </span>
                        <span v-else>-</span>

                        <button
                          v-if="isNonEmptyString(action.note)"
                          class="btn btn-white btn-xs copy-button"
                          @click.stop="copyToClipboard(action.note)"
                          title="Kopiuj do schowka"
                        >
                          <i class="fa fa-copy"></i>
                        </button>
                      </div>
                    </div>
                  </template>

                  <!-- Default column rendering -->
                  <template v-else>
                    {{
                      column.key.includes('.')
                        ? getNestedValue(duplicate, column.key) ?? '-'
                        : duplicate[column.key] ?? '-'
                    }}
                  </template>
                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else>
        <p class="text-center mt-3">Brak elementów do wyświetlenia</p>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
